import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  CssBaseline,
  List,
  ListItem,
  useMediaQuery,
  styled,
} from "@mui/material";

/* =====================
   STYLED COMPONENTS
===================== */

const HeaderContainer = styled("header")(({ theme }) => ({
  position: "fixed",
  top: 0,
  width: "100%",
  backgroundColor: theme.theme_pallete.secondary_color,
  zIndex: 1300,
  padding: "32px 0 24px",
  textAlign: "center",
}));

const Name = styled(Link)(() => ({
  fontFamily: "Oswald",
  fontSize: "36px",
  fontWeight: 500,
  letterSpacing: "1px",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
  transition: "opacity 0.2s ease",
  
  "&:hover": {
    opacity: 0.7,
  },
}));

const Nav = styled("nav")(() => ({
  marginTop: "24px",
  display: "flex",
  justifyContent: "center",
  gap: "48px",
}));

const NavLink = styled(Link)(({ theme, active }) => ({
  textDecoration: "none",
  fontFamily: "Oswald",
  fontSize: "14px",
  letterSpacing: "1px",
  color: theme.theme_pallete.primary_color,
  paddingBottom: "6px",
  borderBottom: active ? `2px solid ${theme.theme_pallete.primary_color}` : "2px solid transparent",
  transition: "border-color 0.2s ease",

  "&:hover": {
    borderBottom: `2px solid ${theme.theme_pallete.primary_color}`,
  },
}));

const ExternalLink = styled("a")(({ theme, active }) => ({
  textDecoration: "none",
  fontFamily: "Oswald",
  fontSize: "14px",
  letterSpacing: "1px",
  color: theme.theme_pallete.primary_color,
  paddingBottom: "6px",
  borderBottom: active ? `2px solid ${theme.theme_pallete.primary_color}` : "2px solid transparent",
  transition: "border-color 0.2s ease",

  "&:hover": {
    borderBottom: `2px solid ${theme.theme_pallete.primary_color}`,
  },
}));

/* =====================
   MOBILE STYLES
===================== */

const MobileHeader = styled("div")(({ theme }) => ({
  position: "fixed",
  top: 0,
  width: "100%",
  padding: "16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: theme.theme_pallete.secondary_color,
  zIndex: 1300,
}));

const NameMobile = styled(Link)(() => ({
  fontFamily: "Oswald",
  fontSize: "18px",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
}));

const MobileMenu = styled(List)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.theme_pallete.secondary_color,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
}));

const MobileLink = styled(Link)(({ theme, active }) => ({
  fontFamily: "Oswald",
  textDecoration: "none",
  fontSize: "16px",
  color: theme.theme_pallete.primary_color,
  fontWeight: active ? 700 : 400,
  borderBottom: active ? `2px solid ${theme.theme_pallete.primary_color}` : "none",
  paddingBottom: active ? "4px" : "0",
}));

const MobileExternalLink = styled("a")(({ theme, active }) => ({
  fontFamily: "Oswald",
  textDecoration: "none",
  fontSize: "16px",
  color: theme.theme_pallete.primary_color,
  fontWeight: active ? 700 : 400,
  borderBottom: active ? `2px solid ${theme.theme_pallete.primary_color}` : "none",
  paddingBottom: active ? "4px" : "0",
}));

/* =====================
   COMPONENT
===================== */

const Header = () => {
  const desktopView = useMediaQuery("(min-width:1093px)");
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();

  const headerItems = [
    { label: "HOME", to: "/" },
    { label: "ABOUT", to: "/about" },
    { label: "WORK", to: "/projects" },
    { label: "TALKS", to: "/speaking" },
    { label: "WRITINGS", to: "/articles" },
    { label: "MEDIA", to: "/videos" },
    { label: "COMMUNITY", to: "/community" },
  ];

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <CssBaseline />

      {desktopView ? (
        <HeaderContainer>
          <Name to="/">AMINAT FOWOKE BALOGUN</Name>

          <Nav>
            {headerItems.map((item) => (
              <NavLink 
                key={item.label} 
                to={item.to}
                active={isActive(item.to) ? 1 : 0}
              >
                {item.label}
              </NavLink>
            ))}

            <ExternalLink
              href="https://drive.google.com/file/d/1mOWNNVBQbgXKS9w1IaqTRm_Nn5ct7ZeU/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
            >
              EXPERIENCE
            </ExternalLink>

            <ExternalLink
              href="mailto:aminatbalogun00@gmail.com?subject=Hi%20Aminat%20Fowoke%20Balogun"
            >
              CONTACT ME
            </ExternalLink>
          </Nav>
        </HeaderContainer>
      ) : (
        <>
          <MobileHeader>
            <NameMobile to="/" onClick={() => setShowMenu(false)}>
              AMINAT FOWOKE BALOGUN
            </NameMobile>

            <Icon
              icon="gg:menu-right"
              style={{ fontSize: 36, cursor: "pointer" }}
              onClick={() => setShowMenu(true)}
            />
          </MobileHeader>

          {showMenu && (
            <MobileMenu>
              {headerItems.map((item) => (
                <ListItem key={item.label}>
                  <MobileLink 
                    to={item.to} 
                    onClick={() => setShowMenu(false)}
                    active={isActive(item.to) ? 1 : 0}
                  >
                    {item.label}
                  </MobileLink>
                </ListItem>
              ))}

              <ListItem>
                <MobileExternalLink
                  href="https://drive.google.com/file/d/1mOWNNVBQbgXKS9w1IaqTRm_Nn5ct7ZeU/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  EXPERIENCE
                </MobileExternalLink>
              </ListItem>

              <ListItem>
                <MobileExternalLink
                  href="mailto:aminatbalogun00@gmail.com?subject=Hi%20Aminat%20Fowoke%20Balogun"
                >
                  CONTACT ME
                </MobileExternalLink>
              </ListItem>

              <Icon
                icon="fluent-mdl2:cancel"
                style={{ fontSize: 32, marginTop: 24, cursor: "pointer" }}
                onClick={() => setShowMenu(false)}
              />
            </MobileMenu>
          )}
        </>
      )}
    </>
  );
};

export default Header;